// EntityProfile.js - Fully aligned with NewCompanyProfile.js
// Includes: listing table, metadata section in modal, expandable mappings, inline edit/delete

import React, { useState, useEffect } from "react";
import axios from "axios";
import DashboardHeader from "../includes/dashboardHeader/DashboardHeader";
import DashboardSidebar from "../includes/dashboardSidebar/DashboardSidebar";
import OrgTabs from "./OrgTabs";
import config from "../../config";
import Select from "react-select";
import VisibilityIcon from '@material-ui/icons/Visibility';
import AlertBox from '../AlertBox.js';
import { startLoader, stopLoader } from "../../actions/loaderActions";
import { useDispatch } from 'react-redux';
import ConfirmDialog from '../ConfirmDialog.js';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import operatingUnitImg from '../../common/images/operating-unit.svg';
import { useHistory } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Collapse,
  Card,
  CardContent,
  CardHeader,
  Divider,
  InputAdornment
} from "@material-ui/core";

import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const ApiBaseUrl = config.NodeApiBaseUrl;
const laravelBaseUrl = config.laravelBaseUrl;

const getDefaultHeaders = () => ({
  "x-tenant-schema": "Amplo111",
  "x-user-id": localStorage.getItem("UserId") || ""
});

const EntityProfile = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const getEmptyEntityForm = () => ({
    name: "",
    type_of_holding: "",
    share_holding_percent: "",
    year_of_incorporation: "",
    registered_address: "",
    city: "",
    country: "",
    state: "",
    description: "",
    website: "",
    revenue_last_fy: "",
    num_employees: "",
    additional_notes: "",
    company_id: "",
    industry_peers: [],
  });
  const typeOfHoldingOptions = [
    { label: "Affiliate", value: "Affiliate" },
    { label: "Franchise", value: "Franchise" },
    { label: "Holding Company", value: "Holding Company" },
    { label: "Joint Venture", value: "Joint Venture" },
    { label: "Subsidiary (Majority owned)", value: "Subsidiary (Majority owned)" },
    { label: "Subsidiary (Wholly owned)", value: "Subsidiary (Wholly owned)" }
  ];

  const getEmptyMapping = () => ({
    company_id: "",
    entity_id: "",
    industry: [],
    mapping_source: "manual",
    activities: [],
    activitiesInput: "",

  });
  const [companies, setCompanies] = useState([]);
  const [previewEntity, setPreviewEntity] = useState(null);
  const [companyEntities, setCompanyEntities] = useState([]);
  const [expandedCompanyIds, setExpandedCompanyIds] = useState([]);
  const [expandedEntityIds, setExpandedEntityIds] = useState([]);
  const [entityMetadata, setEntityMetadata] = useState({});
  const [editEntityId, setEditEntityId] = useState(null);
  const [showEntityModal, setShowEntityModal] = useState(false);
  const [newEntity, setNewEntity] = useState(getEmptyEntityForm());
  const [industryMapping, setIndustryMapping] = useState(getEmptyMapping());
  const [industries, setIndustries] = useState([]);
  const [yearInput, setYearInput] = useState(() => {
    const yoi = newEntity.year_of_incorporation;
    if (yoi && yoi.includes('-')) {
      return new Date(yoi).getFullYear().toString();
    }
    return yoi || '';
  });
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    onConfirm: () => { },
  });

  const [entitySortConfig, setEntitySortConfig] = useState({ key: '', direction: 'asc' });
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [formInitialState, setFormInitialState] = useState(getEmptyEntityForm());
  const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);


  useEffect(() => {
    fetchCompanies();
    fetchIndustries();
  }, []);

  useEffect(() => {
    axios.get('https://countriesnow.space/api/v0.1/countries/positions')
      .then((res) => {
        const countries = res.data.data
          .map(c => ({ label: c.name, value: c.name }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountryOptions(countries);

      })
      .catch(err => console.error('Failed to load countries', err));
  }, []);

  useEffect(() => {
    if (selectedCountry) {
      axios.post('https://countriesnow.space/api/v0.1/countries/states', {
        country: selectedCountry
      })
        .then((res) => {
          const states = res.data.data.states
            .map(s => ({ label: s.name, value: s.name }))
            .sort((a, b) => a.label.localeCompare(b.label));
          setStateOptions(states);
        })
        .catch(err => console.error('Failed to load states', err));
    } else {
      setStateOptions([]);
    }
  }, [selectedCountry]);

  const fetchCompanies = async () => {
    dispatch(startLoader());
    const res = await axios.get(`${ApiBaseUrl}getAllCompanies`, { headers: getDefaultHeaders() });
    setCompanies(res.data?.data || []);
    setExpandedCompanyIds(res.data?.data.map(company => company.company_id));
    dispatch(stopLoader());
  };

  const fetchIndustries = async () => {
    try {
      const res = await axios.get(`${laravelBaseUrl}industry-collection`, {
        headers: getDefaultHeaders()
      });
      setIndustries(res.data.data || []);
    } catch (err) {
      dispatch(stopLoader());
      console.error("Error fetching industries", err);
    }
  };

  const fetchAllEntitiesByCompanyID = async () => {
    dispatch(startLoader());
    const grouped = [];
    for (const company of companies) {
      const res = await axios.get(`${ApiBaseUrl}getAllEntities/${company.company_id}`, { headers: getDefaultHeaders() });
      if (res.data?.data?.length) grouped.push({ company, entities: res.data.data });
    }
    setCompanyEntities(grouped);
    dispatch(stopLoader());
  };

  useEffect(() => {
    if (companies.length) fetchAllEntitiesByCompanyID();
  }, [companies]);

  const handleEditEntity = async (entity) => {
    dispatch(startLoader());
    try {
      const res = await axios.get(`${ApiBaseUrl}getGroupedIndustryMetadata`, {
        headers: getDefaultHeaders(),
        params: {
          companyId: entity.company_id,
          entityId: entity.entity_id,
        }
      });

      const mappings = res.data?.data || [];
      setSelectedCountry(entity.country);

      setFormInitialState({
        ...entity,
        industry_peers: Array.isArray(entity.entity_peers)
          ? [...entity.entity_peers.map(p => ({ name: p.name || "", website: p.website || "" }))]
          : [],
        industry_mappings: mappings.map((m) => ({
          ...m,
          activitiesInput: (m.activities || []).join(', ')
        })),
        state: entity.state || '',
      });


      setNewEntity({
        ...entity,
        industry_peers: Array.isArray(entity.entity_peers)
          ? [...entity.entity_peers.map(p => ({ name: p.name || "", website: p.website || "" }))]
          : [],
        industry_mappings: mappings.map((m) => ({
          ...m,
          activitiesInput: (m.activities || []).join(', ')
        })),
        state: entity.state || '',
      });


      const year = entity.year_of_incorporation
        ? new Date(entity.year_of_incorporation).getFullYear().toString()
        : '';
      setYearInput(year);

      setIndustryMapping(getEmptyMapping()); // reset single mapping if needed
      setEditEntityId(entity.entity_id);
      setShowEntityModal(true);
      dispatch(stopLoader());
    } catch (err) {
      dispatch(stopLoader());
      console.error("❌ Error loading entity mappings:", err);
      showAlert('error', 'Failed to load industry mappings for this entity.');
    }
  };



  const handleDeleteEntity = (entityId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Entity?',
      content: 'Are you sure you want to delete this entity?',
      onConfirm: async () => {
        try {
          await axios.delete(`${ApiBaseUrl}deleteEntity/${entityId}`, {
            headers: getDefaultHeaders(),
          });
          fetchAllEntitiesByCompanyID();
        } catch (err) {
          console.error('Error deleting entity', err);
          showAlert('error', 'Error deleting entity.');
        } finally {
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        }
      },
    });
  };

  const toggleCompanyExpand = (companyId) => {
    setExpandedCompanyIds((prev) =>
      prev.includes(companyId) ? prev.filter((id) => id !== companyId) : [...prev, companyId]
    );
  };


  const handleEntitySubmit = async (e) => {
    e.preventDefault();
    dispatch(startLoader());
    try {
      const entityPayload = { ...newEntity };

      const url = editEntityId
        ? `${ApiBaseUrl}updateEntity/${editEntityId}`
        : `${ApiBaseUrl}createEntity`;

      const method = editEntityId ? "put" : "post";

      const entityResponse = await axios[method](url, entityPayload, {
        headers: getDefaultHeaders()
      });

      const entity_id = editEntityId
        ? editEntityId
        : entityResponse?.data?.entity_id || entityResponse?.data?.id;

      if (!entity_id) {
        showAlert('warning', 'Entity saved, but no entity_id returned.');
        return;
      }

      // ✅ FIX: Attach entity_id to payload to be used in metadata loop
      entityPayload.entity_id = entity_id;

      // ✅ Submit industry metadata mappings
      for (const mapping of newEntity.industry_mappings || []) {
        const cleanedPeers = (mapping.industry_peers || []).map((peer) => {
          if (typeof peer === "string") {
            return { name: peer, website: "" };
          } else if (typeof peer === "object") {
            return {
              peer_id: peer.peer_id || undefined,
              name: peer.name || "",
              website: peer.website || ""
            };
          } else {
            return { name: "", website: "" };
          }
        });

        const payload = {
          entity_id: entityPayload.entity_id,  // ✅ now defined
          company_id: entityPayload.company_id,
          level: "entity",
          industry: mapping.industry || [],
          activities: mapping.activities || [],
          industry_peers: cleanedPeers,
          display_industry: mapping.display_industry || ""
        };

        if (mapping.record_id) {
          await axios.put(`${ApiBaseUrl}updateIndustryGroupedMetadata`, {
            record_id: mapping.record_id,
            ...payload
          }, {
            headers: getDefaultHeaders()
          });
        } else {
          await axios.post(`${ApiBaseUrl}saveIndustryGroupedMetadata`, payload, {
            headers: getDefaultHeaders()
          });
        }
      }

      showAlert('success', editEntityId ? 'Entity & Mappings Updated!' : 'Entity & Mappings Created!');
      setShowEntityModal(false);
      setEditEntityId(null);
      setNewEntity(getEmptyEntityForm());
      setIndustryMapping(getEmptyMapping());
      fetchAllEntitiesByCompanyID();
      dispatch(stopLoader());
    } catch (err) {
      dispatch(stopLoader());
      console.error("❌ Error during entity or mapping save:", err);

      const message =
        err?.response?.data?.message ||
        err?.response?.statusText ||
        'Error saving entity or mappings.';

      showAlert('error', message);
    }
  };


  const handleDeletePeer = (idx) => {
    const updated = [...newEntity.industry_peers];
    const peer = updated[idx];

    if (peer.peer_id) {
      setConfirmDialog({
        open: true,
        title: 'Delete Peer?',
        content: 'This peer exists on the server. Delete it?',
        onConfirm: async () => {
          try {
            await axios.delete(`${ApiBaseUrl}deleteCompanyPeer/${peer.peer_id}`, {
              headers: getDefaultHeaders(),
            });
            showAlert('success', 'Peer deleted from server.');
          } catch (err) {
            console.error('❌ Failed to delete peer:', err);
            showAlert('error', 'Error deleting peer.');
            setConfirmDialog((prev) => ({ ...prev, open: false }));
            return;
          }

          updated.splice(idx, 1);
          setNewEntity({ ...newEntity, industry_peers: updated });
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        },
      });
    } else {
      // Just remove from local state
      updated.splice(idx, 1);
      setNewEntity({ ...newEntity, industry_peers: updated });
    }
  };

  const handleAddMapping = () => {
    const mappings = Array.isArray(newEntity.industry_mappings) ? newEntity.industry_mappings : [];
    setNewEntity({
      ...newEntity,
      industry_mappings: [...mappings, getEmptyMapping()],
    });
  };

  const handleDeleteMapping = (idx) => {
    const mapping = newEntity.industry_mappings[idx];

    if (mapping.record_id) {
      setConfirmDialog({
        open: true,
        title: 'Delete Mapping?',
        content: 'This mapping exists on the server. Do you want to delete it?',
        onConfirm: async () => {
          try {
            await axios.delete(`${ApiBaseUrl}deleteGroupedIndustryMetadata`, {
              headers: getDefaultHeaders(),
              data: { record_id: mapping.record_id },
            });
            showAlert('success', 'Mapping deleted from server.');
          } catch (err) {
            console.error('❌ Failed to delete mapping:', err);
            showAlert('error', 'Error deleting mapping.');
            setConfirmDialog((prev) => ({ ...prev, open: false }));
            return;
          }

          const updated = [...newEntity.industry_mappings];
          updated.splice(idx, 1);
          setNewEntity({ ...newEntity, industry_mappings: updated });
          setConfirmDialog((prev) => ({ ...prev, open: false }));
        },
      });
    } else {
      // Locally delete if not saved on server
      const updated = [...newEntity.industry_mappings];
      updated.splice(idx, 1);
      setNewEntity({ ...newEntity, industry_mappings: updated });
    }
  };


  const openEntityPreviewModal = async (entity) => {
    dispatch(startLoader());
    try {
      setPreviewEntity(entity)
      dispatch(stopLoader());
    } catch (err) {
      dispatch(stopLoader());
      console.error("❌ Error loading industry mappings for entity preview:", err);
      showAlert('error', 'Failed to load full data for entity preview.');
    }
  };

  const showAlert = (type, msg) => {
    setAlert({ open: true, message: msg, severity: type });
  };

  const handleClose = () => setAlert(prev => ({ ...prev, open: false }));

  const handleEntitySort = (key) => {
    setEntitySortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleClick = () => {
    history.push('/new-unit');
  };



  return (
    <>
      <DashboardHeader />
      <Box display="flex">
        <DashboardSidebar />
        <Box component="main" flexGrow={1} p={3} mt={5}>
          <Box display="flex" justifyContent="space-between" mb={3} alignItems="center">
            <Typography variant="h5">Organization Structure</Typography>
            <Box display="flex" alignItems="center">
              <img
                src={require("./../../common/images/diva-icon.png")}
                alt="Logo"
                style={{ marginRight: 8, height: 24 }}
              />
              <Typography variant="caption">Powered by Amploglobal</Typography>
            </Box>
          </Box>
          <OrgTabs />

          <Box mt={4} justifyContent="space-between" alignItems="center">
            <div className="card-header d-flex justify-content-between">
              <span>Entity Profile</span>
              <Button variant="contained" color="primary" onClick={() => {
                setNewEntity(getEmptyEntityForm());
                setShowEntityModal(true);
              }}>
                + Add New Entity
              </Button>
            </div>
          </Box>
          <Box mt={4}>
            {companyEntities.map(({ company, entities }) => (
              <Card key={company.company_id} variant="outlined" style={{ marginBottom: 16 }}>
                <CardHeader
                  title={company.name}
                  action={<IconButton onClick={() => toggleCompanyExpand(company.company_id)}>{expandedCompanyIds.includes(company.company_id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>}
                />
                <Collapse in={expandedCompanyIds.includes(company.company_id)} timeout="auto" unmountOnExit>
                  <CardContent>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sortDirection={entitySortConfig.key === 'name' ? entitySortConfig.direction : false}>
                              <TableSortLabel
                                active={true}
                                direction={entitySortConfig.key === 'name' ? entitySortConfig.direction : 'asc'}
                                onClick={() => handleEntitySort('name')}
                                hideSortIcon={false}
                              >
                                Entity Name
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={entitySortConfig.key === 'city' ? entitySortConfig.direction : false}>
                              <TableSortLabel
                                active={true}
                                direction={entitySortConfig.key === 'city' ? entitySortConfig.direction : 'asc'}
                                onClick={() => handleEntitySort('city')}
                                hideSortIcon={false}
                              >
                                City
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={entitySortConfig.key === 'type_of_holding' ? entitySortConfig.direction : false}>
                              <TableSortLabel
                                active={true}
                                direction={entitySortConfig.key === 'type_of_holding' ? entitySortConfig.direction : 'asc'}
                                onClick={() => handleEntitySort('type_of_holding')}
                                hideSortIcon={false}
                              >
                                Type of Holding
                              </TableSortLabel>
                            </TableCell>
                            <TableCell># Operating Unit</TableCell>
                            <TableCell>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {[...entities]
                            .sort((a, b) => {
                              if (!entitySortConfig.key) return 0;
                              const aValue = a[entitySortConfig.key]?.toString().toLowerCase() || '';
                              const bValue = b[entitySortConfig.key]?.toString().toLowerCase() || '';
                              if (aValue < bValue) return entitySortConfig.direction === 'asc' ? -1 : 1;
                              if (aValue > bValue) return entitySortConfig.direction === 'asc' ? 1 : -1;
                              return 0;
                            })
                            .map((entity) => (
                              <React.Fragment key={entity.entity_id}>
                                <TableRow>
                                  <TableCell>
                                    <span
                                      onClick={() => openEntityPreviewModal(entity)}
                                      style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                                    >
                                      {entity.name}
                                    </span>
                                  </TableCell>
                                  <TableCell>{entity.city}</TableCell>
                                  <TableCell>{entity.type_of_holding}</TableCell>
                                  <TableCell>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                      <div
                                        onClick={handleClick}
                                        style={{
                                          cursor: 'pointer',
                                          backgroundImage: `url(${operatingUnitImg})`,
                                          backgroundSize: 'contain',
                                          backgroundRepeat: 'no-repeat',
                                          backgroundPosition: 'center',
                                          width: '60px',
                                          height: '36px',
                                          position: 'relative',
                                        }}
                                      >
                                        <div
                                          style={{
                                            position: 'absolute',
                                            left: '75%',
                                            transform: 'translateX(-50%)',
                                            background: 'rgb(0, 115, 165)',
                                            color: 'white',
                                            borderRadius: '20px',
                                            fontSize: '13px',
                                            fontWeight: 'bold',
                                            minWidth: '35px',
                                            textAlign: 'center',
                                            padding: '2px 0px',
                                            bottom: '0px',
                                          }}

                                        >
                                          {entity.entity_level_operating_units?.length}
                                        </div>

                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Tooltip title="Edit">
                                      <IconButton onClick={() => handleEditEntity(entity)}><EditIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    <IconButton onClick={() => openEntityPreviewModal(entity)}>
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                    <Tooltip title="Delete">
                                      <IconButton onClick={() => handleDeleteEntity(entity.entity_id)}><DeleteIcon fontSize="small" color="secondary" /></IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                                {expandedEntityIds.includes(entity.entity_id) && entityMetadata[entity.entity_id]?.length > 0 && (
                                  <TableRow>
                                    <TableCell colSpan={5}>
                                      {entityMetadata[entity.entity_id].map((m, idx) => (
                                        <Card key={idx} variant="outlined" style={{ margin: '10px 0' }}>
                                          <CardContent>
                                            <Grid container spacing={2}>
                                              <Grid item xs={12} md={3}>
                                                <Typography variant="subtitle2" color="textSecondary">Industries</Typography>
                                                <Typography>{(m.industry || []).join(', ') || '-'}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={3}>
                                                <Typography variant="subtitle2" color="textSecondary">Activities</Typography>
                                                <Typography>{(m.activities || []).join(', ') || '-'}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={4}>
                                                <Typography variant="subtitle2" color="textSecondary">Industry Peers</Typography>
                                                <Typography>{(m.industry_peers || []).join(', ') || '-'}</Typography>
                                              </Grid>
                                              <Grid item xs={12} md={2}>
                                                <Typography variant="subtitle2" color="textSecondary">Level</Typography>
                                                <Typography>{m.level || '-'}</Typography>
                                              </Grid>
                                            </Grid>
                                          </CardContent>
                                        </Card>
                                      ))}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Collapse>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Modal same as in NewCompanyProfile with metadata section */}
      <Dialog open={showEntityModal}
        onClose={() => {
          if (isEqual(newEntity, formInitialState)) {
            setShowEntityModal(false);
          } else {
            setConfirmDialog({
              open: true,
              title: 'Unsaved Changes',
              content: 'Changes will be lost. Do you want to exit?',
              onConfirm: () => {
                setShowEntityModal(false);
                setConfirmDialog({ ...confirmDialog, open: false });
              }
            });
          }
        }}
        maxWidth="md" fullWidth>
        <DialogTitle>{editEntityId ? "Edit Entity" : "Add New Entity"}</DialogTitle>
        <IconButton
          onClick={() => {
            if (isEqual(newEntity, formInitialState)) {
              setShowEntityModal(false);
            } else {
              setConfirmDialog({
                open: true,
                title: 'Unsaved Changes',
                content: 'Changes will be lost. Do you want to exit?',
                onConfirm: () => {
                  setShowEntityModal(false);
                  setConfirmDialog({ ...confirmDialog, open: false });
                }
              });
            }
          }}
          style={{
            position: 'absolute',
            right: 0,
            top: '15px',
            color: '#888',
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        <form onSubmit={handleEntitySubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Company */}
              <Grid item xs={6}>
                <InputLabel>Associated Company</InputLabel>
                <Select
                  options={companies.map(c => ({ label: c.name, value: c.company_id }))}
                  value={companies.find(c => c.company_id === newEntity.company_id) ? {
                    label: companies.find(c => c.company_id === newEntity.company_id).name,
                    value: newEntity.company_id
                  } : null}
                  onChange={(selected) => setNewEntity({ ...newEntity, company_id: selected?.value || "" })}
                />
              </Grid>

              {/* Type of Holding */}
              <Grid item xs={6}>
                <InputLabel>Type of Holding</InputLabel>
                <Select
                  options={typeOfHoldingOptions}
                  value={typeOfHoldingOptions.find(opt => opt.value === newEntity.type_of_holding) || null}
                  onChange={(selected) =>
                    setNewEntity({ ...newEntity, type_of_holding: selected?.value || "" })
                  }
                  placeholder="Select Holding Type"
                />
              </Grid>

              {/* Entity Name */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={newEntity.name}
                  onChange={(e) => setNewEntity({ ...newEntity, name: e.target.value })}
                />
              </Grid>

              {/* Share Holding Percent */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Share Holding Percent"
                  value={newEntity.share_holding_percent}
                  onChange={(e) => setNewEntity({ ...newEntity, share_holding_percent: e.target.value })}
                />
              </Grid>

              {/* Registered Address */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Registered Address"
                  value={newEntity.registered_address}
                  onChange={(e) => setNewEntity({ ...newEntity, registered_address: e.target.value })}
                />
              </Grid>


              {/* Description */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newEntity.description}
                  onChange={(e) => setNewEntity({ ...newEntity, description: e.target.value })}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={4}>
                <InputLabel>Country</InputLabel>
                <Select
                  options={countryOptions}
                  value={countryOptions.find(opt => opt.value === newEntity.country) || null}
                  onChange={(selected) => {
                    setSelectedCountry(selected.value);
                    setNewEntity({ ...newEntity, country: selected.value, state: '' });
                  }}
                  placeholder="Select Country"
                />
              </Grid>

              {/* State */}
              <Grid item xs={4}>
                <InputLabel>State</InputLabel>
                <Select
                  options={stateOptions}
                  value={stateOptions.find(opt => opt.value === newEntity.state) || null}
                  onChange={(selected) =>
                    setNewEntity({ ...newEntity, state: selected?.value || '' })
                  }
                  placeholder="Select State"
                  isDisabled={!stateOptions.length}
                />
              </Grid>

              {/* City */}
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={newEntity.city}
                  onChange={(e) => setNewEntity({ ...newEntity, city: e.target.value })}
                />
              </Grid>

              {/* Website */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Website"
                  value={newEntity.website}
                  onChange={(e) => setNewEntity({ ...newEntity, website: e.target.value })}
                />
              </Grid>


              {/* Revenue Last FY */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Revenue Last FY"
                  value={newEntity.revenue_last_fy}
                  onChange={(e) => setNewEntity({ ...newEntity, revenue_last_fy: e.target.value })}
                />
              </Grid>

              {/* Number of Employees */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Employees"
                  value={newEntity.num_employees}
                  onChange={(e) => setNewEntity({ ...newEntity, num_employees: e.target.value })}
                />
              </Grid>

              {/* Additional Notes */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  value={newEntity.additional_notes}
                  onChange={(e) => setNewEntity({ ...newEntity, additional_notes: e.target.value })}
                />
              </Grid>

              {/* Year of Incorporation */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Year of Incorporation"
                  type="number"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                  value={yearInput}
                  onChange={(e) => {
                    const year = e.target.value;
                    setYearInput(year);
                    if (year.length === 4) {
                      setNewEntity({
                        ...newEntity,
                        year_of_incorporation: `${year}-01-01`,
                      });
                    } else if (year === '') {
                      setNewEntity({
                        ...newEntity,
                        year_of_incorporation: '',
                      });
                    }
                  }}
                />
              </Grid>


              {/* Industry peer */}
              <Divider></Divider>
              <Grid item xs={12}>
                <Card elevation={2}>
                  <Box p={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Industry Peers</Typography>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          const updated = [...(newEntity.industry_peers || [])];
                          updated.push({ name: "", website: "" });
                          setNewEntity({ ...newEntity, industry_peers: updated });
                        }}
                        style={{ textTransform: 'none', width: '135px' }}
                      >
                        + Add Peer
                      </Button>
                    </Box>

                    {(newEntity.industry_peers || []).map((peer, idx) => (
                      <Card key={idx} variant="outlined" style={{ marginBottom: 16 }}>
                        <Box p={2}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={5}>
                              <TextField
                                label="Peer Name"
                                fullWidth
                                value={peer.name}
                                onChange={(e) => {
                                  const updated = [...newEntity.industry_peers];
                                  updated[idx].name = e.target.value;
                                  setNewEntity({ ...newEntity, industry_peers: updated });
                                }}
                              />
                            </Grid>
                            <Grid item xs={5}>
                              <TextField
                                label="Website"
                                fullWidth
                                value={peer.website}
                                onChange={(e) => {
                                  const updated = [...newEntity.industry_peers];
                                  updated[idx].website = e.target.value;
                                  setNewEntity({ ...newEntity, industry_peers: updated });
                                }}
                              />
                            </Grid>
                            <Grid item xs={2} textAlign="end">
                              <Button
                                variant="outlined"
                                color="secondary"
                                style={{ textAlign: 'end' }}
                                onClick={() => handleDeletePeer(idx)}
                              >
                                Delete
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Card>
                    ))}
                  </Box>
                </Card>
              </Grid>
              <Divider></Divider>
              {/* Industry Mapping */}
              <Grid item xs={12}>
                <Box mt={3}>
                  <Card elevation={2}>
                    <Box p={3}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Industry</Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleAddMapping}
                          style={{ textTransform: "none" }}
                        >
                          + Add Industry
                        </Button>
                      </Box>

                      {Array.isArray(newEntity.industry_mappings) &&
                        newEntity.industry_mappings.map((m, idx) => (
                          <Card key={idx} variant="outlined" style={{ marginBottom: 24 }}>
                            <Box p={3}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="subtitle1">Industry #{idx + 1}</Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"

                                  onClick={() => handleDeleteMapping(idx)}
                                >
                                  Delete
                                </Button>
                              </Box>

                              <Box mb={2}>
                                <InputLabel>Industries</InputLabel>
                                <Select
                                  name="industry"
                                  options={industries
                                    .sort((a, b) => a.valueToShow.localeCompare(b.valueToShow))
                                    .map((i) => ({
                                      label: i.valueToShow,
                                      value: i.valueToShow,
                                    }))}
                                  value={industries
                                    .map((i) => ({ label: i.valueToShow, value: i.valueToShow }))
                                    .find((opt) => opt.value === (m.industry?.[0] || "")) || null}
                                  onChange={(selected) => {
                                    const updated = [...newEntity.industry_mappings];
                                    updated[idx].industry = selected ? [selected.value] : [];
                                    setNewEntity({ ...newEntity, industry_mappings: updated });
                                  }}
                                  menuPortalTarget={document.body} // attaches the menu to body
                                  styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 1300 }),
                                    menuList: (base) => ({
                                      ...base,
                                      maxHeight: 150,
                                      overflowY: 'auto',
                                    }),
                                  }}
                                />

                              </Box>

                              <Box>
                                <TextField
                                  label="Activities (comma separated)"
                                  fullWidth
                                  variant="outlined"
                                  value={
                                    m.activitiesInput !== undefined
                                      ? m.activitiesInput
                                      : (m.activities?.join(", ") || "")
                                  }
                                  onChange={(e) => {
                                    const input = e.target.value;
                                    const updated = [...newEntity.industry_mappings];
                                    updated[idx].activitiesInput = input;
                                    setNewEntity({ ...newEntity, industry_mappings: updated });
                                  }}
                                  onBlur={(e) => {
                                    const input = e.target.value;
                                    const updated = [...newEntity.industry_mappings];
                                    const parsed = input.split(",").map(a => a.trim()).filter(a => a);
                                    updated[idx].activities = parsed;
                                    updated[idx].activitiesInput = parsed.join(", "); // Sync the visible value
                                    setNewEntity({ ...newEntity, industry_mappings: updated });
                                  }}
                                />
                              </Box>
                              <Box mt={2}>
                            <TextField
                              fullWidth
                              label="Display Industry (optional)"
                              variant="outlined"
                              value={m.display_industry || ""}
                              onChange={(e) => {
                                const updated = [...newEntity.industry_mappings];
                                updated[idx].display_industry = e.target.value;
                                setNewEntity({ ...newEntity, industry_mappings: updated });
                              }}
                            />
                          </Box>
                            </Box>
                          </Card>
                        ))}
                    </Box>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              if (isEqual(newEntity, formInitialState)) {
                setShowEntityModal(false);
              } else {
                setConfirmDialog({
                  open: true,
                  title: 'Unsaved Changes',
                  content: 'Changes will be lost. Do you want to exit?',
                  onConfirm: () => {
                    setShowEntityModal(false);
                    setConfirmDialog({ ...confirmDialog, open: false });
                  }
                });
              }
            }} color="secondary">Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editEntityId ? "Update Entity" : "Save Entity"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={!!previewEntity} onClose={() => setPreviewEntity(null)} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: '#CEDBE6', color: '#fff', fontWeight: 600,marginTop: 15 }}>
          Entity Preview
          <IconButton
          onClick={() => {
            setPreviewEntity(null)
          }}
          style={{
            position: 'absolute',
            right: 0,
            top: '25px',
            color: '#888',
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: '#f5f7fa', padding: '24px 32px' }}>
          <Box>
            {/* Entity Basic Info */}
            <Card elevation={2} style={{ borderRadius: 12, marginBottom: 32, padding: 24 }}>
              <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16 }}>
                {previewEntity?.name || '-'}
              </Typography>
              <Grid container spacing={3}>
                {[
                  { label: 'Website', value: previewEntity?.website },
                  { label: 'City', value: previewEntity?.city },
                  { label: 'Country', value: previewEntity?.country },
                  { label: 'State', value: previewEntity?.state },
                  { label: 'Revenue', value: previewEntity?.revenue_last_fy },
                  { label: 'Employees', value: previewEntity?.num_employees },
                  {
                    label: 'Year of Incorporation',
                    value: previewEntity?.year_of_incorporation
                      ? new Date(previewEntity.year_of_incorporation).getFullYear()
                      : null,
                  },
                ].map((field, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Typography variant="subtitle2" color="textSecondary">{field.label}</Typography>
                    <Typography>{field.value || '-'}</Typography>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                  <Typography>{previewEntity?.description || '-'}</Typography>
                </Grid>
              </Grid>
            </Card>

            {/* Entity Peers */}
            {Array.isArray(previewEntity?.entity_peers) && previewEntity.entity_peers.length > 0 && (
              <Box>
                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12 }}>
                  Entity Peers
                </Typography>
                <Card elevation={1} variant="outlined" style={{ borderRadius: 12, marginBottom: 24, padding: 24 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Peer</Typography>
                      <Box display="flex" flexWrap="wrap">
                        {previewEntity.entity_peers.map((peer, idx) => (
                          <Box
                            key={idx}
                            style={{
                              padding: '6px 14px',
                              marginRight: 8,
                              marginBottom: 8,
                              backgroundColor: '#e3f2fd',
                              borderRadius: 20,
                              fontSize: 14,
                            }}
                          >
                            {peer.name || '-'}
                          </Box>
                        ))}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>Website</Typography>
                      <Box display="flex" flexWrap="wrap">
                        {previewEntity.entity_peers.map((peer, idx) => (
                          <Box
                            key={idx}
                            style={{
                              padding: '6px 14px',
                              marginRight: 8,
                              marginBottom: 8,
                              backgroundColor: '#fff3e0',
                              borderRadius: 20,
                              fontSize: 14,
                            }}
                          >
                            {peer.website ? (
                              <a href={peer.website} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#3f51b5' }}>
                                {peer.website}
                              </a>
                            ) : '-'}
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            )}

            {/* Industry Mappings */}
            {Array.isArray(previewEntity?.entity_level_industry_mappings) &&
              previewEntity.entity_level_industry_mappings.length > 0 && (
                <Box>
                  <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12 }}>
                    Industry
                  </Typography>
                  <Card elevation={1} variant="outlined" style={{ borderRadius: 12, marginBottom: 24, padding: 24 }}>
                    <Grid container spacing={4}>
                      {previewEntity.entity_level_industry_mappings.map((mapping, idx) => (
                        <Grid item xs={12} key={idx}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Box display="flex" flexWrap="wrap">
                                {(mapping.industry || []).map((ind, i) => (
                                  <Box key={i} style={{
                                    padding: '6px 14px',
                                    marginRight: 8,
                                    marginBottom: 8,
                                    backgroundColor: '#e3f2fd',
                                    borderRadius: 20,
                                    fontSize: 14,
                                  }}>
                                    {typeof ind === 'object' ? ind.industry_name : ind}
                                  </Box>
                                ))}
                                {(!mapping.industry || mapping.industry.length === 0) && <Typography>-</Typography>}
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Activities</Typography>
                              <Box display="flex" flexWrap="wrap">
                                {(mapping.activities || []).map((act, i) => (
                                  <Box key={i} style={{
                                    padding: '6px 14px',
                                    marginRight: 8,
                                    marginBottom: 8,
                                    backgroundColor: '#fff3e0',
                                    borderRadius: 20,
                                    fontSize: 14,
                                  }}>
                                    {act}
                                  </Box>
                                ))}
                                {(!mapping.activities || mapping.activities.length === 0) && <Typography>-</Typography>}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                </Box>
              )}


            {/* Operating Units */}
            {Array.isArray(previewEntity?.entity_level_operating_units) && previewEntity.entity_level_operating_units.length > 0 && (
              <Box>
                <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 12 }}>
                  Operating Units
                </Typography>
                {previewEntity.entity_level_operating_units.map((unit, idx) => (
                  <Card key={idx} elevation={1} variant="outlined" style={{ borderRadius: 12, marginBottom: 24, padding: 24 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                      {unit.name || '-'}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Products</Typography>
                        <Box display="flex" flexWrap="wrap">
                          {(unit.products || []).map((prod, i) => (
                            <Box
                              key={i}
                              style={{
                                padding: '6px 14px',
                                marginRight: 8,
                                marginBottom: 8,
                                backgroundColor: '#e8f5e9',
                                borderRadius: 20,
                                fontSize: 14,
                              }}
                            >
                              {prod}
                            </Box>
                          ))}
                          {(!unit.products || unit.products.length === 0) && <Typography>-</Typography>}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="textSecondary">Services</Typography>
                        <Box display="flex" flexWrap="wrap">
                          {(unit.services || []).map((srv, i) => (
                            <Box
                              key={i}
                              style={{
                                padding: '6px 14px',
                                marginRight: 8,
                                marginBottom: 8,
                                backgroundColor: '#ede7f6',
                                borderRadius: 20,
                                fontSize: 14,
                              }}
                            >
                              {srv}
                            </Box>
                          ))}
                          {(!unit.services || unit.services.length === 0) && <Typography>-</Typography>}
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions style={{ padding: '12px 24px' }}>
          <Button onClick={() => setPreviewEntity(null)} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <AlertBox
        open={alert.open}
        onClose={handleClose}
        message={alert.message}
        severity={alert.severity}
      />
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        content={confirmDialog.content}
        onCancel={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
      />


    </>
  );
};

export default EntityProfile;

